import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Button } from './ui/button';
import { X, ChevronRight, ChevronLeft, Sparkles, PartyPopper, Rocket, Lightbulb, CheckCircle, SkipForward } from 'lucide-react';
import confetti from 'canvas-confetti';

export const SpotlightOverlay = ({ targetRect, isVisible }) => {
  if (!isVisible || !targetRect) return null;

  // Mode flottant : pas de spotlight, simple overlay sombre.
  if (targetRect._isFloating) {
    return createPortal(
      <div className="fixed inset-0 z-[9998] pointer-events-none bg-black/65" />,
      document.body,
    );
  }

  const padding = 8;
  const borderRadius = 12;

  return createPortal(
    <div className="fixed inset-0 z-[9998] pointer-events-none">
      <svg className="w-full h-full">
        <defs>
          <mask id="spotlight-mask">
            <rect x="0" y="0" width="100%" height="100%" fill="white" />
            <rect
              x={targetRect.left - padding}
              y={targetRect.top - padding}
              width={targetRect.width + padding * 2}
              height={targetRect.height + padding * 2}
              rx={borderRadius}
              fill="black"
            />
          </mask>
        </defs>
        <rect
          x="0"
          y="0"
          width="100%"
          height="100%"
          fill="rgba(0, 0, 0, 0.75)"
          mask="url(#spotlight-mask)"
        />
        {/* Glow effect around spotlight */}
        <rect
          x={targetRect.left - padding - 2}
          y={targetRect.top - padding - 2}
          width={targetRect.width + padding * 2 + 4}
          height={targetRect.height + padding * 2 + 4}
          rx={borderRadius + 2}
          fill="none"
          stroke="url(#spotlight-gradient)"
          strokeWidth="3"
          className="animate-pulse"
        />
        <defs>
          <linearGradient id="spotlight-gradient" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#fbbf24" />
            <stop offset="50%" stopColor="#f97316" />
            <stop offset="100%" stopColor="#fbbf24" />
          </linearGradient>
        </defs>
      </svg>
    </div>,
    document.body
  );
};

// Tooltip Component
export const TourTooltip = ({ step, currentStep, totalSteps, position, targetRect, onNext, onPrev, onSkip, onFinish, tourConfig }) => {
  const [tooltipStyle, setTooltipStyle] = useState({});
  const tooltipRef = useRef(null);

  useEffect(() => {
    if (!targetRect || !tooltipRef.current) return;

    const tooltip = tooltipRef.current;
    const tooltipRect = tooltip.getBoundingClientRect();
    const padding = 20;

    let top, left;

    // Mode flottant (target introuvable) : centrer la tooltip à l'écran.
    if (targetRect._isFloating) {
      top = (window.innerHeight - tooltipRect.height) / 2;
      left = (window.innerWidth - tooltipRect.width) / 2;
      setTooltipStyle({ top: Math.max(20, top), left: Math.max(20, left) });
      return;
    }

    switch (position) {
      case 'right':
        top = targetRect.top + (targetRect.height / 2) - (tooltipRect.height / 2);
        left = targetRect.right + padding;
        break;
      case 'left':
        top = targetRect.top + (targetRect.height / 2) - (tooltipRect.height / 2);
        left = targetRect.left - tooltipRect.width - padding;
        break;
      case 'top':
        top = targetRect.top - tooltipRect.height - padding;
        left = targetRect.left + (targetRect.width / 2) - (tooltipRect.width / 2);
        break;
      case 'bottom':
      default:
        top = targetRect.bottom + padding;
        left = targetRect.left + (targetRect.width / 2) - (tooltipRect.width / 2);
        break;
    }

    // Keep tooltip in viewport
    const maxLeft = window.innerWidth - tooltipRect.width - 20;
    const maxTop = window.innerHeight - tooltipRect.height - 20;
    left = Math.max(20, Math.min(left, maxLeft));
    top = Math.max(20, Math.min(top, maxTop));

    setTooltipStyle({ top, left });
  }, [targetRect, position]);

  const isLastStep = currentStep === totalSteps - 1;
  const isFirstStep = currentStep === 0;

  return createPortal(
    <div
      ref={tooltipRef}
      className="fixed z-[9999] w-80 animate-in fade-in zoom-in duration-300"
      style={tooltipStyle}
    >
      <div className={`bg-gradient-to-br ${tourConfig.color} p-1 rounded-2xl shadow-2xl`}>
        <div className="bg-white rounded-xl p-5">
          {/* Header */}
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <span className="text-2xl">{step.icon}</span>
              <div>
                <p className="text-xs text-gray-500 font-medium">
                  Étape {currentStep + 1}/{totalSteps}
                </p>
                <h4 className="font-bold text-gray-900">{step.title}</h4>
              </div>
            </div>
            <button
              onClick={onSkip}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Content */}
          <p className="text-gray-600 text-sm leading-relaxed mb-4">
            {step.content}
          </p>

          {/* Progress Bar */}
          <div className="h-1.5 bg-gray-100 rounded-full mb-4 overflow-hidden">
            <div
              className={`h-full bg-gradient-to-r ${tourConfig.color} transition-all duration-500`}
              style={{ width: `${((currentStep + 1) / totalSteps) * 100}%` }}
            />
          </div>

          {/* Navigation */}
          <div className="flex items-center justify-between">
            <Button
              variant="ghost"
              size="sm"
              onClick={onPrev}
              disabled={isFirstStep}
              className="text-gray-500"
            >
              <ChevronLeft className="w-4 h-4 mr-1" />
              Précédent
            </Button>

            <Button
              variant="ghost"
              size="sm"
              onClick={onSkip}
              className="text-gray-400 hover:text-gray-600"
            >
              <SkipForward className="w-4 h-4 mr-1" />
              Passer
            </Button>

            {isLastStep ? (
              <Button
                size="sm"
                onClick={onFinish}
                className={`bg-gradient-to-r ${tourConfig.color} text-white hover:opacity-90`}
              >
                Terminer
                <PartyPopper className="w-4 h-4 ml-1" />
              </Button>
            ) : (
              <Button
                size="sm"
                onClick={onNext}
                className={`bg-gradient-to-r ${tourConfig.color} text-white hover:opacity-90`}
              >
                Suivant
                <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};

// Welcome Modal Component
export const WelcomeModal = ({ tourConfig, onStart, onSkip }) => {
  return createPortal(
    <div className="fixed inset-0 z-[9999] flex items-center justify-center">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" />

      {/* Modal */}
      <div className="relative w-full max-w-lg mx-4 animate-in zoom-in duration-300">
        <div className={`bg-gradient-to-br ${tourConfig.color} p-1 rounded-3xl shadow-2xl`}>
          <div className="bg-white rounded-[22px] p-8 text-center">
            {/* Icon */}
            <div className={`w-20 h-20 mx-auto mb-6 rounded-2xl bg-gradient-to-br ${tourConfig.color} flex items-center justify-center text-4xl shadow-lg`}>
              {tourConfig.icon}
            </div>

            {/* Title */}
            <h2 className="text-2xl font-bold text-gray-900 mb-2">
              {tourConfig.title}
            </h2>
            <p className="text-gray-500 mb-6">
              {tourConfig.subtitle}
            </p>

            {/* Features Preview */}
            <div className="grid grid-cols-3 gap-3 mb-8">
              {tourConfig.steps.slice(0, 3).map((step, i) => (
                <div
                  key={i}
                  className="p-3 bg-gray-50 rounded-xl text-center"
                >
                  <span className="text-2xl mb-1 block">{step.icon}</span>
                  <p className="text-xs text-gray-600 font-medium">{step.title}</p>
                </div>
              ))}
            </div>

            {/* Info */}
            <div className="flex items-center justify-center gap-2 text-sm text-gray-500 mb-6">
              <Lightbulb className="w-4 h-4 text-amber-500" />
              <span>{tourConfig.steps.length} étapes • ~2 minutes</span>
            </div>

            {/* Actions */}
            <div className="flex gap-3">
              <Button
                variant="outline"
                onClick={onSkip}
                className="flex-1"
              >
                Plus tard
              </Button>
              <Button
                onClick={onStart}
                className={`flex-1 bg-gradient-to-r ${tourConfig.color} text-white hover:opacity-90`}
              >
                <Rocket className="w-4 h-4 mr-2" />
                Commencer
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};

// Completion Modal Component
export const CompletionModal = ({ tourConfig, onClose }) => {
  useEffect(() => {
    // Confetti celebration
    confetti({
      particleCount: 100,
      spread: 70,
      origin: { y: 0.6 }
    });
  }, []);

  return createPortal(
    <div className="fixed inset-0 z-[9999] flex items-center justify-center">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />

      {/* Modal */}
      <div className="relative w-full max-w-md mx-4 animate-in zoom-in duration-300">
        <div className={`bg-gradient-to-br ${tourConfig.color} p-1 rounded-3xl shadow-2xl`}>
          <div className="bg-white rounded-[22px] p-8 text-center">
            {/* Success Icon */}
            <div className="w-24 h-24 mx-auto mb-6 rounded-full bg-gradient-to-br from-green-400 to-emerald-500 flex items-center justify-center shadow-lg animate-bounce">
              <CheckCircle className="w-12 h-12 text-white" />
            </div>

            {/* Title */}
            <h2 className="text-2xl font-bold text-gray-900 mb-2">
              Félicitations ! 🎉
            </h2>
            <p className="text-gray-500 mb-6">
              Vous avez terminé le tour guidé. Vous êtes prêt à utiliser votre espace !
            </p>

            {/* Stats */}
            <div className="flex items-center justify-center gap-6 mb-6">
              <div className="text-center">
                <p className="text-3xl font-bold text-gray-900">{tourConfig.steps.length}</p>
                <p className="text-xs text-gray-500">Fonctionnalités</p>
              </div>
              <div className="w-px h-12 bg-gray-200" />
              <div className="text-center">
                <p className="text-3xl font-bold text-green-500">✓</p>
                <p className="text-xs text-gray-500">Terminé</p>
              </div>
            </div>

            {/* Action */}
            <Button
              onClick={onClose}
              className={`w-full bg-gradient-to-r ${tourConfig.color} text-white hover:opacity-90`}
            >
              <Sparkles className="w-4 h-4 mr-2" />
              Commencer !
            </Button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};

// Check if we're in a test/preview environment
